import { css } from '@linaria/core';

import { NeuronsCost } from '~components/NeuronsBadge/NeuronsBadge.styles';

export const fadeIn = css`
	@keyframes animate-fade {
		0% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}
	animation: animate-fade 0.2s cubic-bezier(0.4, 0, 0.6, 1);
`;

export const coverDiv = `
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
`;

export const withHover = css`
	position: relative;
	&:before {
		content: '';
		${coverDiv};
		height: 22px;
		max-width: 220px;
		top: calc(50% - 11px);
		background: var(--blur-bg);
		filter: blur(32px);
		opacity: 0;
		border-radius: 999px;
		will-change: left, top, width, height, filter, transform;
	}
	@media (hover: hover), (-moz-touch-enabled: 0), (pointer: fine) {
		&:hover {
			&:before {
				transition: opacity 0.2s cubic-bezier(0.17, 0.67, 0.83, 0.67);
				opacity: 1;
			}
		}
	}
`;

export const hoverable = `
	user-select: none;
	cursor: pointer;
`;

export const visualHideScrollbar = `
	scrollbar-width: none;
	-ms-overflow-style: none;

	&::-webkit-scrollbar {
		display: none;
	}
`;

export const withBlurBg = ({
	blur = 100,
	opacity = true,
	botGradient = false,
}: {
	blur?: number;
	opacity?: boolean;
	botGradient?: boolean;
}) => `
	position: relative;
	&:before {
		content: '';
		border-radius: 200px;
		z-index:0;
		${coverDiv};
		background: ${
			botGradient
				? 'linear-gradient(90deg, rgb(from var(--bot-color-main) r g b / 80%) 0%, rgba(21, 70, 76, 0.80) 100%)'
				: 'var(--blur-bg)'
		};
		opacity: ${opacity ? 0.75 : 'unset'};
		filter: blur(${blur}px);
		-webkit-transform: translate3d(0,0,0);
	}
`;

export const FormGrid = css`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	column-gap: 8px;
	row-gap: 12px;

	& + button {
		margin-top: 32px;
	}

	& ~ button {
		margin-top: 24px;
		max-width: 100%;
	}
`;

export const truncateString = `
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
`;

export const gradientText = `
	background: linear-gradient(90deg, #ff8ef9 0.01%, #3478f6 99.98%);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
`;

export const liquidAnimation = css`
	@media (pointer: fine) {
		&::before {
			content: '';
			position: absolute;
			z-index: -1;
			width: 100%;
			height: 100%;
			background: linear-gradient(
				45deg,
				var(--brand-pink) 0%,
				var(--brand-blue) 100%
			);
			opacity: 0;
			border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
			transition: filter 0.2s, transform 0.3s, opacity 0.2s;
			filter: blur(10px);
		}

		&:hover {
			&::before {
				opacity: 0.7;
				transform: scale(1.15);
				animation: liquid 6s ease-in-out infinite;
			}
		}
	}

	@keyframes liquid {
		0%,
		100% {
			border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
			background-size: 100% 100%;
		}
		33% {
			border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
			background-size: 300% 150%;
		}
		66% {
			border-radius: 50% 60% 30% 60% / 30% 60% 70% 50%;
			background-size: 150% 300%;
		}
	}
`;

export const paidPreviewAnimation = css`
	@media (pointer: fine) {
		&:hover {
			& > img {
				animation: pulse 1.5s ease-in-out infinite;
			}

			& ${NeuronsCost} {
				transition: transform 0.3s;
				transform: scale(1.1);

				& > img {
					animation: pulse 0.7s ease-in-out infinite,
						glow 2.1s ease-in-out infinite;
				}
			}
		}
	}

	@keyframes pulse {
		0%,
		100% {
			transform: scale(1);
		}
		50% {
			transform: scale(1.1);
		}
	}

	@keyframes glow {
		0%,
		100% {
			filter: drop-shadow(0) brightness(1);
		}
		50% {
			filter: drop-shadow(0 0 4px var(--color-white-06)) brightness(1.5);
		}
	}
`;
